/**
 * Application state: ContentNode / InfluenceNode / Relationship (design D16;
 * av-methodology-matrix-ui spec) — three distinct entities, not one node
 * type with style variants.
 *
 * Persisted to localStorage under a namespace distinct from the bundled JSON
 * knowledge base — clearing or updating one never affects the other.
 */

import type { ArchetypeId, ContentNode, InfluenceNode, Relationship, RelevanceId } from "../domain/types";
import { isKnownArchetypeId, isKnownRelevanceId } from "../domain/classification";

const STORAGE_KEY = "metodologia-av:content-items:v2";

// A small, fixed, distinguishable palette — round-robin assigned to new
// ContentNodes (design D16: "cores diferentes para cada conteúdo").
const COLOR_PALETTE = [
  "#00a89d", // teal
  "#f15a29", // orange
  "#8cc63e", // green
  "#26235e", // navy
  "#c2185b", // magenta
  "#6a4c93", // purple
  "#e0a800", // amber
  "#1976d2", // blue
];

export interface StoreState {
  contents: ContentNode[];
  influences: InfluenceNode[];
  relationships: Relationship[];
}

interface PersistedState extends StoreState {
  version: 2;
}

function emptyState(): StoreState {
  return { contents: [], influences: [], relationships: [] };
}

function isValidRelevance(value: unknown): value is RelevanceId {
  return typeof value === "string" && isKnownRelevanceId(value);
}

function isValidArchetype(value: unknown): value is ArchetypeId {
  return typeof value === "string" && isKnownArchetypeId(value);
}

function isValidWeight(value: unknown): value is 1 | 2 | 3 {
  return value === 1 || value === 2 || value === 3;
}

function isValidContent(value: unknown): value is ContentNode {
  if (typeof value !== "object" || value === null) return false;
  const c = value as Record<string, unknown>;
  return (
    typeof c.id === "string" &&
    typeof c.label === "string" &&
    typeof c.color === "string" &&
    isValidArchetype(c.archetypeId) &&
    isValidRelevance(c.relevance) &&
    isValidWeight(c.weight)
  );
}

function isValidInfluence(value: unknown): value is InfluenceNode {
  if (typeof value !== "object" || value === null) return false;
  const i = value as Record<string, unknown>;
  return (
    typeof i.id === "string" &&
    typeof i.contentId === "string" &&
    isValidArchetype(i.archetypeId) &&
    isValidRelevance(i.relevance) &&
    isValidWeight(i.weight)
  );
}

function isValidRelationship(value: unknown): value is Relationship {
  if (typeof value !== "object" || value === null) return false;
  const r = value as Record<string, unknown>;
  return typeof r.id === "string" && typeof r.sourceContentId === "string" && typeof r.targetContentId === "string";
}

/** Read the full store. Corrupt/missing localStorage data fails gracefully to empty state. */
export function loadState(): StoreState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return emptyState();
    const parsed = JSON.parse(raw) as PersistedState;
    if (!parsed || parsed.version !== 2) return emptyState();
    const contents = Array.isArray(parsed.contents) ? parsed.contents.filter(isValidContent) : [];
    const influences = Array.isArray(parsed.influences) ? parsed.influences.filter(isValidInfluence) : [];
    const relationships = Array.isArray(parsed.relationships)
      ? parsed.relationships.filter(isValidRelationship)
      : [];
    return { contents, influences, relationships };
  } catch {
    return emptyState();
  }
}

function persist(state: StoreState): void {
  const toSave: PersistedState = { version: 2, ...state };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(toSave));
}

export function loadContentNodes(): ContentNode[] {
  return loadState().contents;
}

export function loadInfluenceNodes(contentId?: string): InfluenceNode[] {
  const influences = loadState().influences;
  return contentId ? influences.filter((i) => i.contentId === contentId) : influences;
}

export function loadRelationships(): Relationship[] {
  return loadState().relationships;
}

export function saveContentNode(content: ContentNode): StoreState {
  const state = loadState();
  const index = state.contents.findIndex((c) => c.id === content.id);
  if (index >= 0) state.contents[index] = content;
  else state.contents.push(content);
  persist(state);
  return state;
}

/** Deletes a content node and cascades: its influences and any relationship referencing it. */
export function deleteContentNode(id: string): StoreState {
  const state = loadState();
  state.contents = state.contents.filter((c) => c.id !== id);
  state.influences = state.influences.filter((i) => i.contentId !== id);
  state.relationships = state.relationships.filter((r) => r.sourceContentId !== id && r.targetContentId !== id);
  persist(state);
  return state;
}

export function saveInfluenceNode(influence: InfluenceNode): StoreState {
  const state = loadState();
  const index = state.influences.findIndex((i) => i.id === influence.id);
  if (index >= 0) state.influences[index] = influence;
  else state.influences.push(influence);
  persist(state);
  return state;
}

export function deleteInfluenceNode(id: string): StoreState {
  const state = loadState();
  state.influences = state.influences.filter((i) => i.id !== id);
  persist(state);
  return state;
}

export function saveRelationship(relationship: Relationship): StoreState {
  const state = loadState();
  const index = state.relationships.findIndex((r) => r.id === relationship.id);
  if (index >= 0) state.relationships[index] = relationship;
  else state.relationships.push(relationship);
  persist(state);
  return state;
}

export function deleteRelationship(id: string): StoreState {
  const state = loadState();
  state.relationships = state.relationships.filter((r) => r.id !== id);
  persist(state);
  return state;
}

export function clearState(): void {
  localStorage.removeItem(STORAGE_KEY);
}

let idCounter = 0;
export function generateId(prefix: string): string {
  idCounter += 1;
  return `${prefix}-${Date.now().toString(36)}-${idCounter}`;
}

/** Round-robin color assignment for a new ContentNode (design D16). */
export function nextColor(): string {
  const existingCount = loadState().contents.length;
  return COLOR_PALETTE[existingCount % COLOR_PALETTE.length];
}

export function colorForContent(contentId: string): string | undefined {
  return loadState().contents.find((c) => c.id === contentId)?.color;
}
